import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { FiCalendar, FiClock, FiMapPin, FiFilter } from 'react-icons/fi';

// Animatie varianten
const fadeIn: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Event interface
interface Event {
  id: number;
  title: string;
  description: string;
  image: string | any; // Ondersteunt zowel string paden als geïmporteerde afbeeldingen
  date: string;
  time: string;
  location: string;
  category: string;
  slug: string;
}

// Evenementen data
const events: Event[] = [
  {
    id: 1,
    title: 'Jaarlijkse Boekenmarkt',
    description: 'Onze jaarlijkse boekenmarkt waar u tweedehands boeken kunt kopen voor het goede doel. De opbrengst gaat naar Hospice Dignitas te Hoorn en Make-A-Wish.',
    image: '/images/boekenmarkt.jpg',
    date: '12 april 2025',
    time: '10:00 - 16:00 uur',
    location: 'Turfhaven te Hoorn',
    category: 'Fundraising',
    slug: 'boekenmarkt'
  },
  {
    id: 2,
    title: 'Bridgen voor het Goede Doel',
    description: 'Een gezellige bridgemiddag georganiseerd door Lions Club Hoorn De Doelen en Bridgeclub Zwaag / Risdam. De opbrengst gaat naar goede doelen.',
    image: '/images/event-bridge.jpg',
    date: '2 november 2025',
    time: '13:00 - 17:00 uur',
    location: 'De Witte Valk, Zwaag',
    category: 'Fundraising',
    slug: 'bridgen'
  },
  {
    id: 3,
    title: 'Vredesposterwedstrijd',
    description: 'Een internationale wedstrijd waarbij kinderen worden aangemoedigd om na te denken over vrede en dit uit te drukken in een poster.',
    image: '/images/event-vredesposter.jpg',
    date: 'September 2025',
    time: 'Inschrijving open',
    location: 'Scholen in Hoorn',
    category: 'Educatie',
    slug: 'vredesposter'
  },
  {
    id: 4,
    title: 'Wijnproeverij',
    description: 'Een exclusieve wijnproeverij met bijpassende hapjes. De opbrengst gaat naar lokale goede doelen.',
    image: '/images/event-wine.jpg',
    date: '15 mei 2025',
    time: '19:30 - 22:30 uur',
    location: 'Restaurant De Nadorst, Blokker',
    category: 'Fundraising',
    slug: 'wijnproeverij'
  },
  {
    id: 5,
    title: 'Golftoernooi',
    description: 'Ons jaarlijkse golftoernooi voor leden en genodigden. De opbrengst gaat naar goede doelen.',
    image: '/images/event-golf.jpg',
    date: '20 juni 2025',
    time: '10:00 - 17:00 uur',
    location: 'Golfbaan Spierdijk',
    category: 'Sport',
    slug: 'golftoernooi'
  },
  {
    id: 6,
    title: 'Kerstdiner',
    description: 'Ons jaarlijkse kerstdiner voor leden en partners. Een gezellige avond met heerlijk eten en entertainment.',
    image: '/images/event-christmas.jpg',
    date: '18 december 2025',
    time: '18:30 - 23:00 uur',
    location: 'Restaurant De Nadorst, Blokker',
    category: 'Sociaal',
    slug: 'kerstdiner'
  }
];

// Categorieën
const categories: string[] = [
  'Alle categorieën',
  'Fundraising',
  'Educatie',
  'Sport',
  'Sociaal'
];

const Events: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('Alle categorieën');
  const [filteredEvents, setFilteredEvents] = useState<Event[]>(events);

  const handleCategoryChange = (category: string): void => {
    setActiveCategory(category);
    
    if (category === 'Alle categorieën') {
      setFilteredEvents(events);
    } else {
      const filtered = events.filter(event => event.category === category);
      setFilteredEvents(filtered);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-lions-dark text-white py-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="/images/events-hero.jpg" 
            alt="Evenementen van Lions Club Hoorn De Doelen" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.h1 
              variants={fadeIn}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Onze <span className="text-lions-yellow">Evenementen</span>
            </motion.h1>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Ontdek de komende evenementen van Lions Club Hoorn De Doelen en doe mee om het goede doel te steunen.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Evenementen Section */}
      <section className="section bg-white dark:bg-lions-dark">
        <div className="container-custom">
          {/* Filter */}
          <div className="mb-12">
            <div className="flex items-center justify-center flex-wrap gap-4">
              <div className="flex items-center text-gray-700 dark:text-gray-300 mr-2">
                <FiFilter className="mr-2" /> Filter:
              </div>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-4 py-2 rounded-full transition-colors duration-300 ${
                    activeCategory === category
                      ? 'bg-lions-blue text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Evenementen Grid */}
          {filteredEvents.length > 0 ? (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredEvents.map((event: Event) => (
                <motion.div
                  key={event.id}
                  variants={fadeIn}
                  className="bg-lions-light dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                >
                  <img 
                    src={event.image} 
                    alt={event.title} 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <span className="inline-block px-3 py-1 bg-lions-yellow text-lions-dark text-sm font-semibold rounded-full mb-4">
                      {event.category}
                    </span>
                    <h3 className="text-xl font-bold mb-2">{event.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4">
                      {event.description}
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-gray-500 dark:text-gray-400">
                        <FiCalendar className="mr-2" /> {event.date}
                      </div>
                      <div className="flex items-center text-gray-500 dark:text-gray-400">
                        <FiClock className="mr-2" /> {event.time}
                      </div>
                      <div className="flex items-center text-gray-500 dark:text-gray-400">
                        <FiMapPin className="mr-2" /> {event.location}
                      </div>
                    </div>
                    <Link to={`/agenda/${event.slug}`} className="btn-primary w-full text-center">
                      Meer informatie
                    </Link>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold mb-2">Geen evenementen gevonden</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Er zijn momenteel geen evenementen in deze categorie. Bekijk alle evenementen.
              </p>
              <button
                onClick={() => handleCategoryChange('Alle categorieën')}
                className="btn-primary"
              >
                Alle evenementen
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-lions-blue text-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeIn}
              className="text-3xl md:text-4xl font-bold mb-6"
            >
              Organiseer een evenement met ons
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-xl mb-8 text-gray-200"
            >
              Wilt u een evenement organiseren samen met Lions Club Hoorn De Doelen? Neem contact met ons op voor de mogelijkheden.
            </motion.p>
            <motion.div 
              variants={fadeIn}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/contact" className="btn-secondary">
                Neem contact op
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Events; 